import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {ArrowLeft, Download, FileText, Plus, Trash2} from 'lucide-react'
import { lumi } from '../lib/lumi'
import jsPDF from 'jspdf'

interface ItemOrcamento {
  descricao: string
  valor: string
}

export default function CriarProposta() {
  const navigate = useNavigate()
  
  // Dados da Empresa
  const [nomeEmpresa, setNomeEmpresa] = useState('')
  const [cpfCnpjEmpresa, setCpfCnpjEmpresa] = useState('')
  const [telefoneEmpresa, setTelefoneEmpresa] = useState('')
  const [emailEmpresa, setEmailEmpresa] = useState('')
  const [enderecoEmpresa, setEnderecoEmpresa] = useState('')
  const [cidadeEmpresa, setCidadeEmpresa] = useState('')
  const [estadoEmpresa, setEstadoEmpresa] = useState('')
  const [cepEmpresa, setCepEmpresa] = useState('')
  const [websiteEmpresa, setWebsiteEmpresa] = useState('')
  const [redesSociais, setRedesSociais] = useState('')
  
  // Dados do Cliente
  const [nomeCliente, setNomeCliente] = useState('')
  const [cpfCnpjCliente, setCpfCnpjCliente] = useState('')
  const [emailCliente, setEmailCliente] = useState('')
  const [telefoneCliente, setTelefoneCliente] = useState('')
  const [enderecoCliente, setEnderecoCliente] = useState('')
  
  // Informações do Projeto
  const [tituloServico, setTituloServico] = useState('')
  const [quemSomos, setQuemSomos] = useState('')
  const [descricaoDetalhada, setDescricaoDetalhada] = useState('')
  const [entregasIncluidas, setEntregasIncluidas] = useState('')
  
  // Valores
  const [itensOrcamento, setItensOrcamento] = useState<ItemOrcamento[]>([{descricao: '', valor: ''}])
  const [valorMaoObra, setValorMaoObra] = useState('')
  const [valorMateriais, setValorMateriais] = useState('')
  const [desconto, setDesconto] = useState('')
  
  // Condições Comerciais
  const [prazoEntrega, setPrazoEntrega] = useState('')
  const [validadeProposta, setValidadeProposta] = useState('15')
  const [formaPagamento, setFormaPagamento] = useState('')
  const [garantia, setGarantia] = useState('')
  
  // Observações
  const [observacoes, setObservacoes] = useState('')
  const [naoIncluso, setNaoIncluso] = useState('')
  const [termosAdicionais, setTermosAdicionais] = useState('')
  
  const [loading, setLoading] = useState(false)
  const [pdfGerado, setPdfGerado] = useState(false)

  const adicionarItem = () => {
    setItensOrcamento([...itensOrcamento, {descricao: '', valor: ''}])
  }

  const removerItem = (index: number) => {
    if (itensOrcamento.length > 1) {
      setItensOrcamento(itensOrcamento.filter((_, i) => i !== index))
    }
  }

  const atualizarItem = (index: number, campo: 'descricao' | 'valor', valor: string) => {
    const novosItens = [...itensOrcamento]
    novosItens[index][campo] = valor
    setItensOrcamento(novosItens)
  }

  const calcularTotal = () => {
    const totalItens = itensOrcamento.reduce((acc, item) => {
      const valor = parseFloat(item.valor.replace(',', '.')) || 0
      return acc + valor
    }, 0)
    const maoObra = parseFloat(valorMaoObra.replace(',', '.')) || 0
    const materiais = parseFloat(valorMateriais.replace(',', '.')) || 0
    const descontoVal = parseFloat(desconto.replace(',', '.')) || 0
    
    const subtotal = totalItens + maoObra + materiais
    const total = subtotal - descontoVal
    return total
  }

  const gerarPDF = async () => {
    if (!nomeEmpresa || !cpfCnpjEmpresa || !telefoneEmpresa || !emailEmpresa ||
        !nomeCliente || !emailCliente || !telefoneCliente ||
        !tituloServico || !descricaoDetalhada || !prazoEntrega || !formaPagamento) {
      alert('Por favor, preencha todos os campos obrigatórios (*)')
      return
    }

    setLoading(true)

    try {
      const user = lumi.auth.user
      if (!user) {
        alert('Usuário não autenticado')
        return
      }

      const numeroProposta = `UNO-${new Date().getFullYear()}-${Date.now().toString().slice(-6)}`
      const total = calcularTotal()

      // Salvar proposta no banco
      const userId = user.id || user.userId || user._id
      console.log('🔍 Salvando proposta com userId:', userId)
      
      await lumi.entities.propostas.create({
        logoEmpresa: '',
        nomeEmpresa,
        nomeCliente,
        emailCliente,
        telefoneCliente,
        servicoOferecido: tituloServico,
        descricaoServico: descricaoDetalhada,
        escopoDetalhado: entregasIncluidas,
        prazo: prazoEntrega,
        valor: total.toFixed(2),
        formaPagamento,
        validadeProposta: parseInt(validadeProposta),
        observacoes,
        status: 'criada',
        statusManual: 'pendente',
        valorGanho: 0,
        creator: userId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })

      // Gerar PDF Profissional
      const doc = new jsPDF()
      
      // Cabeçalho - Título grande
      doc.setFontSize(20)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(0, 0, 0)
      doc.text('PROPOSTA COMERCIAL', 20, 20)
      
      // Número da proposta
      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(100, 100, 100)
      doc.text(`Nº ${numeroProposta} | Validade: ${validadeProposta} dias`, 20, 27)
      
      // Linha separadora vermelha
      doc.setDrawColor(139, 0, 0)
      doc.setLineWidth(0.8)
      doc.line(20, 31, 190, 31)
      
      let yPos = 38
      
      // SEÇÃO: QUEM SOMOS
      doc.setFontSize(10)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(139, 0, 0)
      doc.text('QUEM SOMOS', 20, yPos)
      
      yPos += 5
      doc.setFontSize(9)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(0, 0, 0)
      doc.text(nomeEmpresa, 20, yPos)
      
      yPos += 4
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(80, 80, 80)
      doc.setFontSize(8)
      doc.text(`${cpfCnpjEmpresa.length <= 14 ? 'CPF' : 'CNPJ'}: ${cpfCnpjEmpresa} | Tel: ${telefoneEmpresa} | ${emailEmpresa}`, 20, yPos)
      
      if (quemSomos) {
        yPos += 4
        doc.setFontSize(8)
        doc.setTextColor(60, 60, 60)
        const quemSomosLines = doc.splitTextToSize(quemSomos, 170)
        doc.text(quemSomosLines, 20, yPos)
        yPos += quemSomosLines.length * 3
      }
      
      // SEÇÃO: DADOS DO CLIENTE
      yPos += 7
      doc.setFontSize(10)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(139, 0, 0)
      doc.text('CLIENTE', 20, yPos)
      
      yPos += 5
      doc.setFontSize(9)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(0, 0, 0)
      doc.text(nomeCliente, 20, yPos)
      
      yPos += 4
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(8)
      doc.setTextColor(80, 80, 80)
      const clienteInfo = cpfCnpjCliente ? `${cpfCnpjCliente.length <= 14 ? 'CPF' : 'CNPJ'}: ${cpfCnpjCliente} | Tel: ${telefoneCliente} | ${emailCliente}` : `Tel: ${telefoneCliente} | ${emailCliente}`
      doc.text(clienteInfo, 20, yPos)
      
      // SEÇÃO: DESCRIÇÃO DO SERVIÇO
      yPos += 7
      doc.setFontSize(10)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(139, 0, 0)
      doc.text('DESCRIÇÃO DO SERVIÇO', 20, yPos)
      
      yPos += 5
      doc.setFontSize(10)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(0, 0, 0)
      doc.text(tituloServico, 20, yPos)
      
      yPos += 5
      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(60, 60, 60)
      const descLines = doc.splitTextToSize(descricaoDetalhada, 170)
      doc.text(descLines, 20, yPos)
      yPos += descLines.length * 3
      
      if (entregasIncluidas) {
        yPos += 4
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(0, 0, 0)
        doc.text('Entregas:', 20, yPos)
        yPos += 3
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(60, 60, 60)
        const entregasLines = doc.splitTextToSize(entregasIncluidas, 170)
        doc.text(entregasLines, 20, yPos)
        yPos += entregasLines.length * 3
      }
      
      // TABELA DE VALORES
      yPos += 6
      doc.setFontSize(10)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(139, 0, 0)
      doc.text('INVESTIMENTO', 20, yPos)
      
      yPos += 6
      
      // Cabeçalho da tabela
      doc.setFillColor(240, 240, 240)
      doc.rect(20, yPos - 3, 170, 6, 'F')
      doc.setFontSize(8)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(0, 0, 0)
      doc.text('DESCRIÇÃO', 22, yPos)
      doc.text('VALOR', 165, yPos)
      
      yPos += 6
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(60, 60, 60)
      doc.setFontSize(8)
      
      // Itens do orçamento
      itensOrcamento.forEach((item) => {
        if (item.descricao && item.valor) {
          doc.text(item.descricao.substring(0, 50), 22, yPos)
          doc.text(`R$ ${item.valor}`, 165, yPos)
          yPos += 4
        }
      })
      
      // Mão de obra
      if (valorMaoObra) {
        doc.text('Mão de Obra', 22, yPos)
        doc.text(`R$ ${valorMaoObra}`, 165, yPos)
        yPos += 4
      }
      
      // Materiais
      if (valorMateriais) {
        doc.text('Materiais/Insumos', 22, yPos)
        doc.text(`R$ ${valorMateriais}`, 165, yPos)
        yPos += 4
      }
      
      // Desconto
      if (desconto) {
        doc.setTextColor(0, 150, 0)
        doc.text('Desconto', 22, yPos)
        doc.text(`- R$ ${desconto}`, 165, yPos)
        yPos += 4
        doc.setTextColor(60, 60, 60)
      }
      
      // Linha do total
      yPos += 1
      doc.setDrawColor(0, 0, 0)
      doc.setLineWidth(0.5)
      doc.line(20, yPos, 190, yPos)
      
      yPos += 5
      doc.setFontSize(10)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(139, 0, 0)
      doc.text('TOTAL', 22, yPos)
      doc.text(`R$ ${total.toFixed(2).replace('.', ',')}`, 165, yPos)
      
      // CONDIÇÕES COMERCIAIS
      yPos += 7
      doc.setFontSize(10)
      doc.setTextColor(139, 0, 0)
      doc.text('CONDIÇÕES COMERCIAIS', 20, yPos)
      
      yPos += 5
      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(60, 60, 60)
      doc.text(`• Prazo: ${prazoEntrega}`, 22, yPos)
      yPos += 4
      doc.text(`• Pagamento: ${formaPagamento}`, 22, yPos)
      if (garantia) {
        yPos += 4
        doc.text(`• Garantia: ${garantia}`, 22, yPos)
      }
      
      // OBSERVAÇÕES
      if (observacoes || naoIncluso || termosAdicionais) {
        yPos += 7
        doc.setFontSize(10)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(139, 0, 0)
        doc.text('OBSERVAÇÕES E TERMOS', 20, yPos)
        yPos += 5
        doc.setFontSize(8)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(60, 60, 60)
        
        if (observacoes) {
          const obsLines = doc.splitTextToSize(`• ${observacoes}`, 168)
          doc.text(obsLines, 22, yPos)
          yPos += obsLines.length * 3 + 2
        }
        if (naoIncluso) {
          const naoInclusoLines = doc.splitTextToSize(`• Não incluso: ${naoIncluso}`, 168)
          doc.text(naoInclusoLines, 22, yPos)
          yPos += naoInclusoLines.length * 3 + 2
        }
        if (termosAdicionais) {
          const termosLines = doc.splitTextToSize(`• ${termosAdicionais}`, 168)
          doc.text(termosLines, 22, yPos)
          yPos += termosLines.length * 3
        }
      }
      
      // Rodapé na mesma página
      doc.setFillColor(0, 0, 0)
      doc.rect(0, 275, 210, 22, 'F')
      
      // Logo pequena no rodapé
      try {
        doc.addImage(logoUrl, 'PNG', 15, 278, 12, 12)
      } catch (e) {
        console.log('Logo no rodapé não carregada')
      }
      
      doc.setFontSize(8)
      doc.setTextColor(255, 255, 255)
      doc.text(nomeEmpresa, 105, 282, { align: 'center' })
      doc.setFontSize(7)
      doc.text(`${telefoneEmpresa} | ${emailEmpresa}`, 105, 287, { align: 'center' })
      if (websiteEmpresa) {
        doc.text(websiteEmpresa, 105, 291, { align: 'center' })
      }
      
      // Download
      doc.save(`Proposta_${numeroProposta}_${nomeCliente.replace(/\s+/g, '_')}.pdf`)
      
      setPdfGerado(true)
      setTimeout(() => {
        navigate('/dashboard')
      }, 2000)
    } catch (error) {
      console.error('Erro ao gerar proposta:', error)
      alert('Erro ao gerar proposta')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#8B0000]">Criar Nova Proposta</h1>
              <p className="text-xs text-gray-600">Gere proposta em PDF</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="space-y-6">
            
            {/* SEÇÃO 1: DADOS DA SUA EMPRESA */}
            <div className="border-b pb-4">
              <h2 className="text-lg font-bold text-[#8B0000] mb-4">1. Dados da Sua Empresa</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Nome da Empresa *</label>
                  <input
                    type="text"
                    value={nomeEmpresa}
                    onChange={(e) => setNomeEmpresa(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Digite o nome da sua empresa"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">CPF ou CNPJ *</label>
                  <input
                    type="text"
                    value={cpfCnpjEmpresa}
                    onChange={(e) => setCpfCnpjEmpresa(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Telefone/WhatsApp *</label>
                    <input
                      type="tel"
                      value={telefoneEmpresa}
                      onChange={(e) => setTelefoneEmpresa(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="(00) 00000-0000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">E-mail *</label>
                    <input
                      type="email"
                      value={emailEmpresa}
                      onChange={(e) => setEmailEmpresa(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="contato@empresa.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Endereço Completo</label>
                  <input
                    type="text"
                    value={enderecoEmpresa}
                    onChange={(e) => setEnderecoEmpresa(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Rua, número, bairro"
                  />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="col-span-1">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Cidade</label>
                    <input
                      type="text"
                      value={cidadeEmpresa}
                      onChange={(e) => setCidadeEmpresa(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="Cidade"
                    />
                  </div>

                  <div className="col-span-1">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Estado</label>
                    <input
                      type="text"
                      value={estadoEmpresa}
                      onChange={(e) => setEstadoEmpresa(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="UF"
                      maxLength={2}
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">CEP</label>
                    <input
                      type="text"
                      value={cepEmpresa}
                      onChange={(e) => setCepEmpresa(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="00000-000"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Website</label>
                    <input
                      type="url"
                      value={websiteEmpresa}
                      onChange={(e) => setWebsiteEmpresa(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="www.suaempresa.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Redes Sociais</label>
                    <input
                      type="text"
                      value={redesSociais}
                      onChange={(e) => setRedesSociais(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="@instagram | @linkedin"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* SEÇÃO 2: DADOS DO CLIENTE */}
            <div className="border-b pb-4">
              <h2 className="text-lg font-bold text-[#8B0000] mb-4">2. Dados do Cliente</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Nome Completo ou Razão Social *</label>
                  <input
                    type="text"
                    value={nomeCliente}
                    onChange={(e) => setNomeCliente(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Digite o nome do cliente"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">CPF ou CNPJ do Cliente</label>
                  <input
                    type="text"
                    value={cpfCnpjCliente}
                    onChange={(e) => setCpfCnpjCliente(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">E-mail *</label>
                    <input
                      type="email"
                      value={emailCliente}
                      onChange={(e) => setEmailCliente(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="cliente@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Telefone/WhatsApp *</label>
                    <input
                      type="tel"
                      value={telefoneCliente}
                      onChange={(e) => setTelefoneCliente(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="(00) 00000-0000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Endereço do Cliente</label>
                  <input
                    type="text"
                    value={enderecoCliente}
                    onChange={(e) => setEnderecoCliente(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Rua, número, bairro, cidade - UF"
                  />
                </div>
              </div>
            </div>

            {/* SEÇÃO 3: INFORMAÇÕES DO PROJETO */}
            <div className="border-b pb-4">
              <h2 className="text-lg font-bold text-[#8B0000] mb-4">3. Informações do Projeto/Serviço</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Título do Serviço/Projeto *</label>
                  <input
                    type="text"
                    value={tituloServico}
                    onChange={(e) => setTituloServico(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Ex: Desenvolvimento de Website Institucional"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">"Quem Somos" - Apresentação da Empresa</label>
                  <textarea
                    value={quemSomos}
                    onChange={(e) => setQuemSomos(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Breve apresentação da sua empresa, experiência, diferenciais..."
                    maxLength={300}
                  />
                  <p className="text-xs text-gray-500 mt-1">{quemSomos.length}/300 caracteres</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Descrição Detalhada do Serviço *</label>
                  <textarea
                    value={descricaoDetalhada}
                    onChange={(e) => setDescricaoDetalhada(e.target.value)}
                    rows={5}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Detalhe o escopo completo do trabalho, etapas, funcionalidades, métodos..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Entregas/Itens Incluídos</label>
                  <textarea
                    value={entregasIncluidas}
                    onChange={(e) => setEntregasIncluidas(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Ex: 3 reuniões de alinhamento, 2 revisões, treinamento de 1 hora..."
                  />
                </div>
              </div>
            </div>

            {/* SEÇÃO 4: VALORES E INVESTIMENTO */}
            <div className="border-b pb-4">
              <h2 className="text-lg font-bold text-[#8B0000] mb-4">4. Valores e Investimento</h2>
              
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-semibold text-gray-700">Itens do Orçamento</label>
                    <button
                      type="button"
                      onClick={adicionarItem}
                      className="flex items-center gap-1 text-xs text-[#8B0000] hover:underline"
                    >
                      <Plus size={14} />
                      Adicionar item
                    </button>
                  </div>
                  
                  {itensOrcamento.map((item, index) => (
                    <div key={index} className="flex gap-2 items-start">
                      <input
                        type="text"
                        value={item.descricao}
                        onChange={(e) => atualizarItem(index, 'descricao', e.target.value)}
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="Descrição do item"
                      />
                      <input
                        type="text"
                        value={item.valor}
                        onChange={(e) => atualizarItem(index, 'valor', e.target.value)}
                        className="w-32 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="Valor"
                      />
                      {itensOrcamento.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removerItem(index)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Mão de Obra (R$)</label>
                    <input
                      type="text"
                      value={valorMaoObra}
                      onChange={(e) => setValorMaoObra(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Materiais/Insumos (R$)</label>
                    <input
                      type="text"
                      value={valorMateriais}
                      onChange={(e) => setValorMateriais(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Desconto (R$)</label>
                  <input
                    type="text"
                    value={desconto}
                    onChange={(e) => setDesconto(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>

                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-bold text-gray-700">TOTAL</span>
                    <span className="text-xl font-bold text-[#8B0000]">R$ {calcularTotal().toFixed(2).replace('.', ',')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* SEÇÃO 5: CONDIÇÕES COMERCIAIS */}
            <div className="border-b pb-4">
              <h2 className="text-lg font-bold text-[#8B0000] mb-4">5. Condições Comerciais</h2>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Prazo de Entrega *</label>
                    <input
                      type="text"
                      value={prazoEntrega}
                      onChange={(e) => setPrazoEntrega(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="Ex: 30 dias ou 15/03/2025"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Validade da Proposta (dias)</label>
                    <input
                      type="text"
                      value={validadeProposta}
                      onChange={(e) => setValidadeProposta(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="15"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Forma de Pagamento *</label>
                  <select
                    value={formaPagamento}
                    onChange={(e) => setFormaPagamento(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  >
                    <option value="">Selecione...</option>
                    <option value="À vista com 10% de desconto">À vista com desconto</option>
                    <option value="Parcelado em 3x sem juros">Parcelado em 3x sem juros</option>
                    <option value="50% entrada + 50% na entrega">50% entrada + 50% na entrega</option>
                    <option value="Pix">Pix</option>
                    <option value="Boleto Bancário">Boleto Bancário</option>
                    <option value="Cartão de Crédito">Cartão de Crédito</option>
                    <option value="Transferência Bancária">Transferência Bancária</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Garantia Oferecida</label>
                  <input
                    type="text"
                    value={garantia}
                    onChange={(e) => setGarantia(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Ex: 90 dias de suporte e ajustes gratuitos"
                  />
                </div>
              </div>
            </div>

            {/* SEÇÃO 6: OBSERVAÇÕES E TERMOS */}
            <div className="pb-4">
              <h2 className="text-lg font-bold text-[#8B0000] mb-4">6. Observações e Termos</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Observações Importantes</label>
                  <textarea
                    value={observacoes}
                    onChange={(e) => setObservacoes(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Informações adicionais relevantes..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">O que NÃO está incluso</label>
                  <textarea
                    value={naoIncluso}
                    onChange={(e) => setNaoIncluso(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Ex: hospedagem, domínio, fotos profissionais..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Termos e Condições Adicionais</label>
                  <textarea
                    value={termosAdicionais}
                    onChange={(e) => setTermosAdicionais(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Cláusulas contratuais, políticas de cancelamento..."
                  />
                </div>
              </div>
            </div>

            <button
              onClick={gerarPDF}
              disabled={loading}
              className="w-full bg-[#8B0000] text-white py-3 rounded-lg font-semibold hover:bg-[#6B0000] transition-colors disabled:bg-gray-400 flex items-center justify-center gap-2 text-sm mt-6"
            >
              {loading ? (
                'Gerando PDF...'
              ) : (
                <>
                  <FileText size={18} />
                  Gerar Proposta Profissional em PDF
                </>
              )}
            </button>

            {pdfGerado && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <Download size={32} className="mx-auto text-green-600 mb-2" />
                <p className="text-sm font-semibold text-green-800">PDF gerado e baixado com sucesso!</p>
                <p className="text-xs text-green-600 mt-1">Redirecionando para o dashboard...</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
